import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { Building2, User, Mail, MapPin, Phone, Calendar, ArrowLeft } from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';
import { validatePassword } from '../utils/passwordValidation';

export default function SuperRegister({ onBack }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role] = useState(4);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    address: '',
    contact_number: '',
    email: '',
    established_year: '',
    gst: '',
  });

  const handleRegister = async () => {
    // Validate password before submitting
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setPasswordError(validation.message);
      notify.error(validation.message);
      return null;
    }
    
    setPasswordError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/superadmin/register/`, {
        username,
        email,
        password,
        role,
      });

      const newUserId = res.data.user_id;
      setError('');
      notify.success('User registered successfully');
      return newUserId;
    } catch (err) {
      setError('Registration failed');
      notify.error('Registration failed');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (idToUse) => {
    const token = localStorage.getItem('accessToken');
    if (!idToUse) {
      notify.error('Please register the user first');
      return;
    }

    console.log('📤 Submitting hospital details:', { ...form, user_id: idToUse });

    try {
      const response = await axios.put(
        `${BASE_URL}/api/hospitals/superadmin/`,
        {
          ...form,
          user_id: idToUse,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log('✅ Hospital details saved:', response.data);
      notify.success('Hospital details saved successfully');
    } catch (err) {
      console.error('❌ Save error:', err.response?.data || err.message);
      console.error('Full error:', err);
      notify.error(err.response?.data?.error || 'Error saving hospital details');
    }
  };

  const handleRegisterAndSubmit = async (e) => {
    e.preventDefault();
    const newUserId = await handleRegister();
    if (newUserId) {
      await handleSubmit(newUserId);
      // Navigate back after successful registration
      if (onBack) {
        setTimeout(() => {
          onBack();
        }, 1500); // Give time for toast to show
      }
    }
  };

  return (
    <div className="space-y-4">
      <Toaster position="top-right" />
      
      {/* Header with Back Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Register Hospital</h2>
            <p className="text-sm text-slate-600">Create a new hospital account with details</p>
          </div>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors border border-slate-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back</span>
          </button>
        )}
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-slate-200 p-6"
      >
        <form onSubmit={handleRegisterAndSubmit} className="space-y-6">
          {/* User Registration Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-primary-400" />
              User Account Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
                <input
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <input
                  type="password"
                  placeholder="Enter password (min 8 chars, uppercase, lowercase, number, special char)"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(''); // Clear error when typing
                  }}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:border-transparent outline-none transition-all ${
                    passwordError ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-medical-500'
                  }`}
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Hospital Details Section */}
          <div className="pt-5 border-t border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary-400" />
              Hospital Details
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Hospital Name
                </label>
                <input
                  name="name"
                  placeholder="Enter hospital name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Address
                </label>
                <input
                  name="address"
                  placeholder="Enter address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Contact
                  </label>
                  <input
                    name="contact_number"
                    placeholder="Enter contact"
                    value={form.contact_number}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Hospital Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Established Year
                  </label>
                  <input
                    name="established_year"
                    placeholder="Enter year"
                    value={form.established_year}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  GST Number
                </label>
                <input
                  name="gst"
                  placeholder="Enter GST number (optional)"
                  value={form.gst}
                  onChange={handleChange}
                  maxLength={15}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <span className="font-medium">Error:</span> {error}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-500/100 hover:bg-medical-600 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading ? 'Saving...' : 'Register & Save Hospital'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


